import React, { useEffect, useState } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { ProfilRocznika } from "./profilRocznika";

// Import logo (dopasuj ścieżkę do swojej struktury projektu)
const logoImg = "./logo_PTH.png"; // Upewnij się, że ten plik istnieje w tej lokalizacji

// --- GLOBALNE STYLE ---
const TeamsGlobalStyle = createGlobalStyle`
  html {
    scroll-behavior: smooth; /* Płynne przewijanie do kotwic */
  }
  body {
    background-color: #ffffff;
    color: #111;
    margin: 0;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
`;

// --- ANIMACJE ---
const pulseAnim = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- STYLED COMPONENTS ---

const PageLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  img {
    height: 80px;
    filter: grayscale(100%);
    margin-bottom: 20px;
    animation: ${pulseAnim} 1.5s infinite ease-in-out;
  }

  span {
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 1.2rem;
    color: #111;
  }
`;

const TeamsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
`;

const TeamsHeaderSection = styled.section`
  padding: 140px 5% 60px 5%;
  background-color: #f9f9f9;
  text-align: center;
  animation: ${slideDown} 0.6s ease-out forwards;

  @media (max-width: 768px) {
    padding: 100px 5% 40px 5%;
  }
`;

const TeamsTitle = styled.h1`
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -2px;
  color: #111;
  margin: 0;
  line-height: 1;
  
  span {
    color: #888;
  }
`;

const TeamsSubtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  margin-top: 20px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const TeamsNavWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px); /* Efekt rozmycia tła pod paskiem */
  border-bottom: 1px solid #eee;
  border-top: 1px solid #eee;
  padding: 0 5%;
`;

const TeamsNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  overflow-x: auto;
  white-space: nowrap;
  padding: 20px 0;
  
  /* Ukrywanie scrollbara */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 25px;
  }
`;

const TeamsNavLabel = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #999;
  font-size: 0.8rem;
`;

const TeamNavLink = styled.a`
  text-decoration: none;
  font-weight: 900;
  font-size: 1.2rem;
  color: #111;
  text-transform: uppercase;
  transition: color 0.3s ease;
  position: relative;

  &:hover {
    color: #888;
  }

  /* Animowana linia pod linkiem przy hoverze */
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #111;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const TeamsContent = styled.div`
  padding-bottom: 100px;
`;

const TeamSection = styled.section`
  scroll-margin-top: 100px; /* Margines dla płynnego przewijania (żeby sticky nav nie zasłonił tytułu) */
`;


// --- KOMPONENT ---

export const RocznikiPage = () => {
  // Stan ładowania identyczny jak na Home.js
  const [isLoading, setIsLoading] = useState(true);

  const [rocznikiD, setRocznikiD] = useState(() => {
    const saved = localStorage.getItem("roczniki");
    return saved ? JSON.parse(saved) : [];
  });

  const fetchRoczniki = async () => {
    try {
      const res = await fetch("https://api.pth-hokej.pl/api/roczniki");
      if (!res.ok) throw new Error("Błąd pobierania");
      const data = await res.json();

      // Opcjonalne sortowanie (np. od najstarszego rocznika do najmłodszego)
      const sortedData = data.sort((a, b) => parseInt(a.rocznik) - parseInt(b.rocznik));

      setRocznikiD(sortedData);
      localStorage.setItem("roczniki", JSON.stringify(sortedData));
    } catch (err) {
      console.error("Błąd API:", err);
      // Jeśli błąd, próbujemy przywrócić z cache (localStorage)
      const cached = localStorage.getItem("roczniki");
      if (cached) {
        setRocznikiD(JSON.parse(cached));
      }
    } finally {
      // Wyłączamy loader niezależnie od tego czy się udało, czy nie
      // Dodano drobne opóźnienie dla estetyki przejścia
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  useEffect(() => {
    fetchRoczniki();
  }, []);

  // Ekran ładowania
  if (isLoading) {
    return (
      <PageLoader>
        <img src={logoImg} alt="Logo Młode Wilki" />
        <span>Wczytywanie Zespołów...</span>
      </PageLoader>
    );
  }

  return (
    <>
      <TeamsGlobalStyle />
      <TeamsContainer>

        {/* HEAD SECTION */}
        <TeamsHeaderSection>
          <TeamsTitle>
            Nasze <span>Zespoły</span>
          </TeamsTitle>
          <TeamsSubtitle>
            Poznaj wszystkie roczniki Młodych Wilków. Od skrzatów po seniorów –
            każda z drużyn reprezentuje nasze barwy z zaangażowaniem i pasją.
          </TeamsSubtitle>
        </TeamsHeaderSection>

        {/* STICKY NAVIGATION BAR */}
        <TeamsNavWrapper>
          <TeamsNav>
            <TeamsNavLabel>Wybierz Rocznik:</TeamsNavLabel>

            {rocznikiD.length > 0 ? (
              rocznikiD.map((r) => (
                <TeamNavLink key={r.rocznik} href={`#rocznik-${r.rocznik}`}>
                  {r.rocznik}
                </TeamNavLink>
              ))
            ) : (
              <span style={{ color: "#999", fontWeight: "bold" }}>Brak danych</span>
            )}
          </TeamsNav>
        </TeamsNavWrapper>

        {/* TEAMS CONTENT */}
        {/* TEAMS CONTENT */}
        <TeamsContent>
          {rocznikiD.map((r) => (
            <TeamSection id={`rocznik-${r.rocznik}`} key={r.rocznik}>
              <ProfilRocznika
                rocznik={r.rocznik}
                trener={r.trener}
                liczbaZawodników={r.liczbaZawodników}
                miejsceTreningów={r.miejsceTreningów}
                miejscaTreningow={r.miejscaGier}
                dniTreningow={r.dniTreningow}
                wyniki={r.wyniki}
                kolorTla1={r.kolor1}
                kolorTla2={r.kolor2}
                rocznikId={r._id}
                zdjecieDruzyny={r.zdjecieDruzyny || ''} /* <--- DODAJ TĘ LINIJKĘ */
              />
            </TeamSection>
          ))}
        </TeamsContent>

      </TeamsContainer>
    </>
  );
};